import apiClient from '@/api/apiClient.ts';

export interface SessionInitOverrides {
  agent: {
    language: string;
    prompt: string | null;
    firstMessage: string | null;
  };
  tts: { voiceId: string | null };
}

export interface SessionInitResponse {
  sessionId: string;
  conversationToken: string;
  agentId: string;
  dynamicVariables: Record<string, string>;
  overrides: SessionInitOverrides;
}

export interface QuickPracticeInitParams {
  simulationType: string;
  salePhase?: string;
  objections?: string;
  sector?: string;
  subSector?: string;
  priceRange?: string;
  difficultyLevel: number;
  language?: string;
}

interface SessionInitRequest {
  simulationId?: string | null;
  quickPractice?: QuickPracticeInitParams;
  maestroId?: string;
}

export class SessionInitError extends Error {
  status: number | null;

  constructor(status: number | null, message: string) {
    super(message);
    this.name = 'SessionInitError';
    this.status = status;
  }
}

const conversationService = {
  /**
   * Starts a session backed by a real Simulation row. Use {@link initQuickPractice}
   * for ad-hoc practice without a persisted simulation.
   */
  async initSession(simulationId: string, maestroId?: string): Promise<SessionInitResponse> {
    return postSessionInit({ simulationId, ...(maestroId ? { maestroId } : {}) });
  },

  async initQuickPractice(params: QuickPracticeInitParams, maestroId?: string): Promise<SessionInitResponse> {
    return postSessionInit({
      simulationId: null,
      quickPractice: params,
      ...(maestroId ? { maestroId } : {}),
    });
  },
};

async function postSessionInit(body: SessionInitRequest): Promise<SessionInitResponse> {
  try {
    const response = await apiClient.post<SessionInitResponse>(
      '/api/conversation/session-init',
      body,
    );
    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status ?? null;
    const serverMessage = (error as { response?: { data?: { title?: string; message?: string } } })
      ?.response?.data;
    const message = serverMessage?.message || serverMessage?.title
      || (error instanceof Error ? error.message : 'SESSION_INIT_FAILED');
    throw new SessionInitError(status, message);
  }
}

export default conversationService;
